import React, { useCallback } from 'react';
import { Box, MenuItem, Select } from '@mui/material';
import FormatColorFillOutlinedIcon from '@mui/icons-material/FormatColorFillOutlined';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';

import type { ShapeElement } from '@/shared/types/slide';
import ToolbarColorPicker from '@/shared/components/ToolbarColorPicker';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ToolbarShapeStyleProps {
  element: ShapeElement;
  onUpdateContent: (patch: Partial<ShapeElement['content']>) => void;
}

// ---------------------------------------------------------------------------
// Stroke width options
// ---------------------------------------------------------------------------

const STROKE_WIDTHS = [0, 1, 2, 3, 4, 5, 8];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ToolbarShapeStyle: React.FC<ToolbarShapeStyleProps> = ({ element, onUpdateContent }) => {
  const isLine = element.content.shapeType === 'line';
  const {
    fillColor = '#3498db',
    strokeColor = isLine ? '#2980b9' : 'transparent',
    strokeWidth = isLine ? 2 : 0,
  } = element.content;

  const handleStrokeWidth = useCallback(
    (e: { target: { value: string | number } }) => {
      onUpdateContent({ strokeWidth: Number(e.target.value) });
    },
    [onUpdateContent]
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {/* Fill color */}
      <ToolbarColorPicker
        value={fillColor}
        onChange={(c) => onUpdateContent({ fillColor: c })}
        label="Fill color"
        icon={<FormatColorFillOutlinedIcon fontSize="small" />}
        allowTransparent
      />

      {/* Stroke color */}
      <ToolbarColorPicker
        value={strokeColor}
        onChange={(c) => onUpdateContent({ strokeColor: c })}
        label="Stroke color"
        icon={<BorderColorOutlinedIcon fontSize="small" />}
      />

      {/* Stroke width */}
      <Select
        size="small"
        value={strokeWidth}
        onChange={handleStrokeWidth}
        sx={{ minWidth: 60, fontSize: 13, height: 32 }}
      >
        {STROKE_WIDTHS.map((w) => (
          <MenuItem key={w} value={w} sx={{ fontSize: 13 }}>
            {w}px
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

export default ToolbarShapeStyle;
